import { type Theme, getColorPalette } from '../../../common/colors/color-palette';
import type { AnnotationLabelT } from '../../../common/types/types';
const getBaseLabelTitle = (title: string): string => {
  const separatorIndex = title.lastIndexOf('_');
  if (separatorIndex === -1) return title;
  const suffix = title.slice(separatorIndex + 1);
  return Number.isInteger(Number(suffix)) ? title.slice(0, separatorIndex) : title;
};

// Assigns each label a hue by its base title's first-seen order, skipping overridden titles.
export const applyM4LabelColors = (annotationLabels: AnnotationLabelT[], theme: Theme, overriddenLabelTitles: ReadonlySet<string> = new Set()): AnnotationLabelT[] => {
  const palette = getColorPalette(theme);
  const hueIndexByBaseTitle = new Map<string, number>();
  annotationLabels.forEach(label => {
    if (overriddenLabelTitles.has(label.title)) {
      return;
    }
    const baseTitle = getBaseLabelTitle(label.title);
    if (!hueIndexByBaseTitle.has(baseTitle)) {
      hueIndexByBaseTitle.set(baseTitle, hueIndexByBaseTitle.size);
    }
  });
  return annotationLabels.map(label => {
    if (overriddenLabelTitles.has(label.title)) {
      return label;
    }
    const hueIndex = hueIndexByBaseTitle.get(getBaseLabelTitle(label.title))!;
    const hue = palette[hueIndex % palette.length]!;
    return {
      ...label,
      style: {
        ...label.style,
        strokeColor: hue.solid,
        fillColor: hue.light
      }
    };
  });
};

// Titles whose stored colors diverge from a fresh hue derivation — the only surviving signal
// that a label was manually recolored, used to rebuild the override set after a storage restore.
export const getM4OverriddenLabelTitles = (annotationLabels: AnnotationLabelT[], theme: Theme): Set<string> => {
  const hueDerivedLabels = applyM4LabelColors(annotationLabels, theme);
  const overriddenTitles = new Set<string>();
  annotationLabels.forEach((label, index) => {
    const hueDerivedLabel = hueDerivedLabels[index]!;
    const isOverridden = label.style.strokeColor !== hueDerivedLabel.style.strokeColor || label.style.fillColor !== hueDerivedLabel.style.fillColor;
    if (isOverridden) {
      overriddenTitles.add(label.title);
    }
  });
  return overriddenTitles;
};